import { Injectable, inject, signal, effect, DestroyRef } from '@angular/core';
import * as THREE from 'three';

import { ViewerCoreService } from './viewer-core.service.js';
import { onViewerEvents } from './event-dispatcher-bridge';
import { InputHandlerService } from './input-handler.service';
import { ProfileWindowControllerService } from './profile-window-controller.service';
import { SceneStateService } from './scene-state.service';
import { SphereVolume } from '../viewer-utils/tools/volume.js';
import { ClipTask, ClipMethod } from '../shared/defines.js';

@Injectable({ providedIn: 'root' })
export class ToolsService {
  private core = inject(ViewerCoreService);
  private sceneState = inject(SceneStateService);
  private inputHandlerService = inject(InputHandlerService);
  private destroyRef = inject(DestroyRef);
  private profileController = inject(ProfileWindowControllerService);

  readonly clipTask = signal<string>('HIGHLIGHT');
  readonly clipMethod = signal<string>('INSIDE_ANY');

  private _syncing = false;

  constructor() {
    effect(() => {
      const viewer = this.core.viewer();
      if (!viewer) return;
      this._syncFromViewer(viewer);

      onViewerEvents(
        viewer,
        {
          cliptask_changed: () => this._pullClipState(viewer),
          clipmethod_changed: () => this._pullClipState(viewer),
        },
        this.destroyRef,
      );
    });

    effect(() => {
      const task = this.clipTask();
      if (this._syncing) return;
      const v = this.core.viewer();
      if (!v) return;
      const value = (ClipTask as any)[task];
      if (value !== undefined) v.setClipTask?.(value);
    });

    effect(() => {
      const method = this.clipMethod();
      if (this._syncing) return;
      const v = this.core.viewer();
      if (!v) return;
      const value = (ClipMethod as any)[method];
      if (value !== undefined) v.setClipMethod?.(value);
    });
  }

  // Measurements

  startDistanceMeasurement(): void {
    const v = this.core.viewer();
    v?.measuringTool?.startInsertion?.({
      showDistances: true,
      showArea: false,
      closed: false,
      name: 'Distance',
    });
  }

  startAngleMeasurement(): void {
    const v = this.core.viewer();
    v?.measuringTool?.startInsertion?.({
      showDistances: false,
      showAngles: true,
      showArea: false,
      closed: true,
      maxMarkers: 3,
      name: 'Angle',
    });
  }

  startPointMeasurement(): void {
    const v = this.core.viewer();
    v?.measuringTool?.startInsertion?.({
      showDistances: false,
      showAngles: false,
      showCoordinates: true,
      showArea: false,
      closed: true,
      maxMarkers: 1,
      name: 'Point',
    });
  }

  startHeightMeasurement(): void {
    const v = this.core.viewer();
    v?.measuringTool?.startInsertion?.({
      showDistances: false,
      showHeight: true,
      showArea: false,
      closed: false,
      maxMarkers: 2,
      name: 'Height',
    });
  }

  startCircleMeasurement(): void {
    const v = this.core.viewer();
    v?.measuringTool?.startInsertion?.({
      showDistances: false,
      showHeight: false,
      showArea: false,
      showCircle: true,
      showEdges: false,
      closed: false,
      maxMarkers: 3,
      name: 'Circle',
    });
  }

  startAzimuthMeasurement(): void {
    const v = this.core.viewer();
    v?.measuringTool?.startInsertion?.({
      showDistances: false,
      showHeight: false,
      showArea: false,
      showCircle: false,
      showEdges: false,
      showAzimuth: true,
      closed: false,
      maxMarkers: 2,
      name: 'Azimuth',
    });
  }

  startAreaMeasurement(): void {
    const v = this.core.viewer();
    v?.measuringTool?.startInsertion?.({
      showDistances: true,
      showArea: true,
      closed: true,
      name: 'Area',
    });
  }

  startProfile(): void {
    const v = this.core.viewer();
    v?.profileTool?.startInsertion?.();
  }

  startVolume(): unknown {
    const v = this.core.viewer();
    return v?.volumeTool?.startInsertion?.();
  }

  startSphereVolume(): unknown {
    const v = this.core.viewer();
    return v?.volumeTool?.startInsertion?.({ type: SphereVolume });
  }

  startAnnotation(): void {
    const v = this.core.viewer();
    v?.annotationTool?.startInsertion?.();
  }

  removeMeasurement(id: string): void {
    const measurements: any[] = this.sceneState.measurements();
    let target: any = null;
    measurements.forEach((mm: any, i: number) => {
      const candidates = [mm.uuid, mm.id, mm.name, `m-${i}`].filter(Boolean);
      if (!target && candidates.includes(id)) target = mm;
    });
    if (target) this.sceneState.removeMeasurement(target);
  }

  removeProfile(id: string): void {
    const profiles: any[] = this.sceneState.profiles();
    let target: any = null;
    profiles.forEach((pp: any, i: number) => {
      const candidates = [pp.uuid, pp.id, pp.name, `p-${i}`].filter(Boolean);
      if (!target && candidates.includes(id)) target = pp;
    });
    if (target) this.sceneState.removeProfile(target);
  }

  removeAnnotation(id: string): void {
    const annRoot = this.sceneState.annotations();
    if (!annRoot) return;
    const children: any[] = annRoot.children || [];
    let target: any = null;
    children.forEach((a: any, i: number) => {
      const candidates = [a.uuid, a.id, a.title, a.name, `a-${i}`].filter(Boolean);
      if (!target && candidates.includes(id)) target = a;
    });
    if (target) {
      annRoot.remove?.(target);
      try {
        target.domElement?.remove?.();
      } catch {
        // ignore DOM cleanup errors
      }
    }
  }

  // Profile window

  showProfile2D(profileId?: string): void {
    const v = this.core.viewer();
    if (!v) return;
    this.profileController.attach(v);

    const profiles = this.sceneState.profiles();
    let target = profiles[0];
    if (profileId) {
      target = profiles.find((p: any) => (p.uuid || p.id) === profileId) || target;
    }
    if (target) {
      this.profileController.setProfile(target);
    }
  }

  getActiveProfile(): unknown {
    return this.profileController.profile || null;
  }

  setAngularProfileWindow(angularProfileWindow: { addPoints?: Function } | any): void {
    const v = this.core.viewer();
    if (!v) return;

    this.profileController.attach(v);
    this.profileController.profileWindow = angularProfileWindow;
  }

  // Clipping — use clipTask/clipMethod signals directly for reactive access

  startClipVolume(): string | null {
    const v = this.core.viewer();
    if (!v?.volumeTool?.startInsertion) return null;
    const vol = v.volumeTool.startInsertion({ clip: true });
    if (vol) {
      try {
        this.inputHandlerService.deselectAll();
        this.inputHandlerService.toggleSelection(vol);
        v.dispatchEvent?.({ type: 'update' });
      } catch {
        /* ignore */
      }
      this.ensureVolumeSyncListeners(vol);
      return vol.uuid || vol.id || vol.name || null;
    }
    return null;
  }

  getClipTaskOptions(): { value: string; label: string }[] {
    return [
      { value: 'HIGHLIGHT', label: 'Highlight' },
      { value: 'SHOW_INSIDE', label: 'Show Inside' },
      { value: 'SHOW_OUTSIDE', label: 'Show Outside' },
    ];
  }

  // Volumes

  listVolumes(): { id: string; name: string; clip: boolean; scale: [number, number, number] }[] {
    const volumes = this.sceneState.volumes();
    if (!volumes.length) return [];
    return volumes
      .filter((vol: any) => vol.clip)
      .map((vol: any) => ({
        id: vol.uuid || vol.id || vol.name,
        name: vol.name,
        clip: !!vol.clip,
        scale: [vol.scale.x, vol.scale.y, vol.scale.z] as [number, number, number],
      }));
  }

  selectVolume(id: string): void {
    const volumes = this.sceneState.volumes();
    if (!volumes.length) return;
    const vol = volumes.find((vol: any) => (vol.uuid || vol.id || vol.name) === id);
    if (!vol) return;
    try {
      this.inputHandlerService.deselectAll();
      this.inputHandlerService.toggleSelection(vol);
    } catch {
      /* ignore */
    }
  }

  getVolume(id: string): any | null {
    const volumes = this.sceneState.volumes();
    if (!volumes.length) return null;
    return volumes.find((vol: any) => (vol.uuid || vol.id || vol.name) === id) || null;
  }

  setVolumeClip(id: string, clip: boolean): void {
    const vol = this.getVolume(id);
    if (!vol) return;
    vol.clip = clip;
  }

  removeVolume(id: string): void {
    const volumes: any[] = this.sceneState.volumes();
    if (!volumes.length) return;
    let vol: any = null;
    volumes.forEach((vv: any, i: number) => {
      const candidates = [vv.uuid, vv.id, vv.name, `v-${i}`].filter(Boolean);
      if (!vol && candidates.includes(id)) vol = vv;
    });
    if (vol) this.sceneState.removeVolume(vol);
  }

  getVolumeDetails(id: string): {
    id: string;
    name: string;
    clip: boolean;
    position: [number, number, number];
    rotationDeg: [number, number, number];
    scale: [number, number, number];
    volume: number;
  } | null {
    const vol = this.getVolume(id);
    if (!vol) return null;
    try {
      const rotVec = vol.rotation?.toVector3?.().toArray?.() || [0, 0, 0];
      const rotDeg: [number, number, number] = [
        (rotVec[0] * 180) / Math.PI,
        (rotVec[1] * 180) / Math.PI,
        (rotVec[2] * 180) / Math.PI,
      ];
      const scale: [number, number, number] = [vol.scale.x, vol.scale.y, vol.scale.z];
      const volumeVal =
        typeof vol.getVolume === 'function'
          ? vol.getVolume()
          : Math.abs(scale[0] * scale[1] * scale[2]);
      return {
        id: vol.uuid || vol.id || vol.name,
        name: vol.name,
        clip: !!vol.clip,
        position: [vol.position.x, vol.position.y, vol.position.z],
        rotationDeg: rotDeg,
        scale,
        volume: volumeVal,
      };
    } catch {
      return null;
    }
  }

  getMeasurementDetails(type: string, id: string): any {
    let obj: any = null;

    if (type === 'measurement') {
      obj = this.sceneState.measurements().find((m: any) => (m.uuid || m.id || m.name) === id);
    } else if (type === 'volume') {
      obj = this.sceneState.volumes().find((vol: any) => (vol.uuid || vol.id || vol.name) === id);
    } else if (type === 'profile') {
      obj = this.sceneState.profiles().find((p: any) => (p.uuid || p.id || p.name) === id);
    } else if (type === 'annotation') {
      const ann = this.sceneState.annotations();
      obj = ann?.children?.find((a: any) => (a.uuid || a.id || a.name) === id);
    }

    if (!obj) return null;

    const details: any = {};

    if (obj.points && obj.points.length > 0) {
      details.coordinates = obj.points.map((p: any) => ({
        x: p.position.x,
        y: p.position.y,
        z: p.position.z,
      }));
    }

    if (obj.getTotalDistance && typeof obj.getTotalDistance === 'function') {
      const positions = obj.points?.map((p: any) => p.position) || [];
      if (positions.length > 1) {
        const distances: number[] = [];
        for (let i = 0; i < positions.length - 1; i++) {
          distances.push(positions[i].distanceTo(positions[i + 1]));
        }
        details.distances = distances;
        details.totalDistance = obj.getTotalDistance();
      }
    }

    if (obj.getArea && typeof obj.getArea === 'function') {
      details.area = obj.getArea();
    }

    if (obj.getAngles && typeof obj.getAngles === 'function') {
      const angles = obj.getAngles();
      if (angles?.length > 0) {
        details.angles = angles.map((angle: any, i: number) => ({
          label: `Angle ${i + 1}`,
          value: (angle * 180) / Math.PI,
        }));
      }
    }

    if (obj.getRadius && typeof obj.getRadius === 'function') {
      details.radius = obj.getRadius();
      details.circumference = 2 * Math.PI * details.radius;
    }

    if (obj.showHeight && obj.points && obj.points.length >= 2) {
      const p0 = obj.points[0].position;
      const p1 = obj.points[1].position;
      details.height = Math.abs(p1.z - p0.z);
    }

    if (type === 'volume') {
      if (typeof obj.getVolume === 'function') {
        details.volume = obj.getVolume();
      }
      if (obj.scale) {
        details.dimensions = { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z };
      }
    }

    if (type === 'annotation') {
      details.title = obj.title || '';
      details.description = obj.description || '';
      if (obj.position) {
        details.position = { x: obj.position.x, y: obj.position.y, z: obj.position.z };
      }
    }

    return details;
  }

  updateAnnotationTitle(id: string, title: string): void {
    const ann = this.sceneState.annotations();
    if (!ann?.children) return;
    const annotation = ann.children.find((a: any) => (a.uuid || a.id || a.name) === id);
    if (annotation) annotation.title = title;
  }

  updateAnnotationDescription(id: string, description: string): void {
    const ann = this.sceneState.annotations();
    if (!ann?.children) return;
    const annotation = ann.children.find((a: any) => (a.uuid || a.id || a.name) === id);
    if (annotation) annotation.description = description;
  }

  // Polygon Clip Volumes

  listPolygonClipVolumes(): { id: string; name: string; initialized: boolean; markers: number }[] {
    const polygons = this.sceneState.polygonClipVolumes();
    if (!polygons.length) return [];
    return polygons.map((p: any) => ({
      id: p.uuid || p.id || p.name,
      name: p.name,
      initialized: !!p.initialized,
      markers: (p.markers || []).length,
    }));
  }

  removePolygonClipVolume(id: string): void {
    const polygons = this.sceneState.polygonClipVolumes();
    if (!polygons.length) return;
    const poly = polygons.find((p: any) => (p.uuid || p.id || p.name) === id);
    if (poly) {
      const v = this.core.viewer();
      v?.scene?.removePolygonClipVolume?.(poly);
    }
  }

  togglePolygonEnabled(id: string): void {
    const polygons = this.sceneState.polygonClipVolumes();
    if (!polygons.length) return;
    const poly = polygons.find((p: any) => (p.uuid || p.id || p.name) === id);
    if (!poly) return;
    poly.enabled = poly.enabled === undefined ? false : !poly.enabled;
    if (poly.enabled && !poly.initialized) {
      poly.enabled = false;
    }
    const v = this.core.viewer();
    v?.dispatchEvent?.({ type: 'update' });
  }

  getPolygonScreenPoints(id: string): { id: string; points: [number, number][] } | null {
    const v = this.core.viewer();
    if (!v?.renderer) return null;
    const polygons = this.sceneState.polygonClipVolumes();
    const poly = polygons.find((p: any) => (p.uuid || p.id || p.name) === id);
    if (!poly?.initialized) return null;
    try {
      const size = v.renderer.getSize(new THREE.Vector2());
      const pts: [number, number][] = (poly.markers || []).map((m: any) => [
        ((m.position.x + 1) / 2) * size.width,
        ((-m.position.y + 1) / 2) * size.height,
      ]);
      return { id: poly.uuid || poly.id || poly.name, points: pts };
    } catch {
      return null;
    }
  }

  getOverlaySize(): { width: number; height: number } {
    const v = this.core.viewer();
    try {
      const size = v?.renderer?.getSize?.(new THREE.Vector2());
      if (size) return { width: size.width, height: size.height };
    } catch {
      /* ignore */
    }
    return { width: 0, height: 0 };
  }

  // Private helpers

  private ensureVolumeSyncListeners(volume: any): void {
    if (!volume || volume.__angularSyncAttached) return;
    const v = this.core.viewer();
    const dispatchRefresh = (): void => {
      v?.dispatchEvent?.({ type: 'update' });
      v?.scene?.dispatchEvent?.({ type: 'update' });
    };
    try {
      volume.addEventListener?.('drag', dispatchRefresh);
      volume.addEventListener?.('drop', dispatchRefresh);
      volume.addEventListener?.('clip_changed', dispatchRefresh);
      volume.addEventListener?.('position_changed', dispatchRefresh);
      volume.addEventListener?.('orientation_changed', dispatchRefresh);
      volume.addEventListener?.('scale_changed', dispatchRefresh);
      volume.__angularSyncAttached = true;
    } catch {
      /* ignore */
    }
  }

  private enumKeyFromValue(enumObj: any, value: any): string {
    if (!enumObj) return '';
    return Object.keys(enumObj).find(k => enumObj[k] === value) || '';
  }

  private _syncFromViewer(viewer: any): void {
    this._pullClipState(viewer);
  }

  private _pullClipState(viewer: any): void {
    this._syncing = true;
    try {
      const task =
        typeof viewer.getClipTask === 'function'
          ? this.enumKeyFromValue(ClipTask, viewer.getClipTask())
          : this.enumKeyFromValue(ClipTask, viewer.clipTask);
      if (task) this.clipTask.set(task);

      const method =
        typeof viewer.getClipMethod === 'function'
          ? this.enumKeyFromValue(ClipMethod, viewer.getClipMethod())
          : this.enumKeyFromValue(ClipMethod, viewer.clipMethod);
      if (method) this.clipMethod.set(method);
    } catch {
      // ignore
    }
    this._syncing = false;
  }
}
